import { useEffect, useState } from 'react';
import {
  getDailyReport,
  reportToCsv,
  type DailyReport,
} from '../../services/reports';

function formatHours(seconds: number): string {
  const h = Math.round(seconds / 3600);
  return `${h}h`;
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function Reports() {
  const [report, setReport] = useState<DailyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [windowHours, setWindowHours] = useState(24);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getDailyReport(windowHours, 10)
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'erro ao carregar relatório');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [windowHours]);

  function handleExport(): void {
    if (!report) return;
    const csv = reportToCsv(report);
    const fname = `oraculo-daily-${report.generated_at.replace(/[:T]/g, '-').replace('Z', '')}.csv`;
    downloadCsv(fname, csv);
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-200">Relatório diário</h2>
          <p className="text-xs text-gray-500 mt-1">
            Sprint 10 — telemetria pura. Em <code>BETA_MODE=log_only</code>, mostra
            o que <em>teria</em> sido decidido sem o beta.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-400 flex items-center gap-2">
            Janela:
            <select
              value={windowHours}
              onChange={(e) => setWindowHours(parseInt(e.target.value, 10))}
              className="rounded border border-[#2a2f36] bg-[#161a1f] text-gray-200 px-2 py-1 text-sm"
            >
              <option value={1}>1h</option>
              <option value={6}>6h</option>
              <option value={24}>24h</option>
              <option value={72}>72h</option>
              <option value={168}>7d</option>
            </select>
          </label>
          <button
            type="button"
            onClick={handleExport}
            disabled={!report || loading}
            className="px-3 py-1.5 text-sm rounded border border-[#2a2f36] bg-[#161a1f] text-gray-200 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Export CSV
          </button>
        </div>
      </header>

      {error && (
        <div className="bg-red-950 border border-red-800 text-red-300 text-sm rounded px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {loading || !report ? (
        <p className="text-gray-500">Carregando...</p>
      ) : (
        <div className="space-y-6">
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card label="Janela" value={formatHours(report.window_seconds)} />
            <Card label="Events totais" value={String(report.events_total)} />
            <Card label="Sessões totais" value={String(report.sessions.total)} />
            <Card label="Risk &gt; 50" value={String(report.sessions.risk_above_50)} />
          </section>

          <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card label="Real FLAG" value={String(report.sessions.decision_real_flag)}
                    color="amber" />
            <Card label="Real KICK" value={String(report.sessions.decision_real_kick)}
                    color="orange" />
            <Card label="Real BLOCK" value={String(report.sessions.decision_real_block)}
                    color="red" />
            <Card label="Diff (response ≠ real)"
                    value={String(report.sessions.decision_real_diff_from_response)}
                    color="amber"
                    hint="Sessões em BETA_MODE — operador escala manualmente" />
          </section>

          <section>
            <h3 className="text-lg font-semibold text-gray-200 mb-3">Top 10 events 24h</h3>
            {report.events_by_type.length === 0 ? (
              <p className="text-gray-500 text-sm">Nenhum evento na janela.</p>
            ) : (
              <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#0d0f12] text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="text-left px-4 py-2">Type</th>
                      <th className="text-right px-4 py-2">Count</th>
                      <th className="text-right px-4 py-2">Critical</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2a2f36]">
                    {report.events_by_type.map((e) => (
                      <tr key={e.event_type}>
                        <td className="px-4 py-2 font-mono text-xs text-gray-300">
                          {e.event_type}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-200">{e.count}</td>
                        <td className="px-4 py-2 text-right text-red-400">
                          {e.critical_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h3 className="text-lg font-semibold text-gray-200 mb-3">
              Top sessões por risk_score
            </h3>
            {report.top_risk_sessions.length === 0 ? (
              <p className="text-gray-500 text-sm">Nenhuma sessão na janela.</p>
            ) : (
              <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#0d0f12] text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="text-left px-4 py-2">UUID</th>
                      <th className="text-left px-4 py-2">HWID</th>
                      <th className="text-left px-4 py-2">IP</th>
                      <th className="text-right px-4 py-2">Risk</th>
                      <th className="text-left px-4 py-2">Decision</th>
                      <th className="text-left px-4 py-2">Real</th>
                      <th className="text-left px-4 py-2">Last HB</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2a2f36]">
                    {report.top_risk_sessions.map((s) => (
                      <tr key={s.session_uuid}>
                        <td className="px-4 py-2 font-mono text-xs text-gray-400" title={s.session_uuid}>
                          {s.session_uuid.slice(0, 8)}…
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-400">{s.hwid || '—'}</td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-400">{s.ip || '—'}</td>
                        <td className="px-4 py-2 text-right text-gray-200">{s.risk_score}</td>
                        <td className="px-4 py-2 text-xs text-gray-300">{s.decision}</td>
                        <td className="px-4 py-2 text-xs text-gray-300">{s.decision_real}</td>
                        <td className="px-4 py-2 text-xs text-gray-400">
                          {new Date(s.last_heartbeat).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <p className="text-xs text-gray-600">
            Gerado em {new Date(report.generated_at).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}

function Card({
  label,
  value,
  color,
  hint,
}: {
  label: string;
  value: string;
  color?: 'amber' | 'orange' | 'red';
  hint?: string;
}) {
  const colors: Record<string, string> = {
    amber: 'text-amber-400',
    orange: 'text-orange-400',
    red: 'text-red-400',
  };
  const valueClass = color ? colors[color] : 'text-gray-100';
  return (
    <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg p-4">
      <p className="text-xs text-gray-500 uppercase">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${valueClass}`}>{value}</p>
      {hint && <p className="text-[10px] text-gray-600 mt-1">{hint}</p>}
    </div>
  );
}
